import { useState } from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowRight, ExternalLink } from "lucide-react";
import { SAMPLE_MEMO_EXCERPT as MEMO } from "@/data/sampleMemoExcerpt";
import { track } from "@/lib/analytics";

const SAMPLE_PDF_URL = `${process.env.REACT_APP_BACKEND_URL}/api/sample-memo.pdf`;

const PRI_COLOR = {
  high: "bg-[#7F1D1D] text-white",
  medium: "bg-[#92400E] text-white",
  low: "bg-[#4B5563] text-white",
};
const PRI_LABEL = { high: "High priority", medium: "Medium", low: "Low" };

const SectionLabel = ({ children }) => (
  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-blueprint mb-3">{children}</div>
);

const Issue = ({ issue, index }) => (
  <article data-testid={`memo-proof-issue-${index}`} className="border-t border-rule pt-6 first:border-t-0 first:pt-0">
    <div className="flex flex-wrap items-center gap-3">
      <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink/55">
        {String(index + 1).padStart(2, "0")}
      </span>
      <h4 className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink">{issue.domain}</h4>
      <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 ${PRI_COLOR[issue.priority]}`}>
        {PRI_LABEL[issue.priority]}
      </span>
    </div>
    <p className="font-serif text-lg sm:text-xl leading-snug tracking-tight text-ink mt-3">{issue.headline}</p>
    {issue.touchpoints?.length > 0 && (
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/55">Regulatory touchpoints</span>
        {issue.touchpoints.map((t) => (
          <span key={t} className="font-mono text-[10px] uppercase tracking-[0.18em] border border-blueprint/40 text-blueprint px-2 py-0.5">{t}</span>
        ))}
      </div>
    )}
    <div className="mt-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/55 mb-2">Diligence questions</div>
      <ul className="space-y-2">
        {issue.questions.map((q) => (
          <li key={q} className="font-mono text-[13px] text-ink/85 leading-relaxed pl-4 border-l border-ink/20">{q}</li>
        ))}
      </ul>
    </div>
    <div className="mt-4 bg-white border-l-2 border-blueprint px-4 py-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-blueprint mb-1.5">Why it matters</div>
      <p className="font-mono text-[13px] text-ink/85 leading-relaxed">{issue.whyItMatters}</p>
    </div>
  </article>
);

/**
 * Proof section: a scrollable preview of the approved, human-vetted October
 * 2026 Issue-Mapping Memorandum (file W3RC-000E3F40). Content mirrors the PDF
 * served at /api/sample-memo.pdf, which is the same file the Starter Kit
 * attaches, so preview, full download, and attachment are one revision.
 */
export const MemoProofSection = ({
  source = "landing",
  heading = "What an Issue-Mapping Memorandum Looks Like.",
  supportingCopy = "Explore a sample CLARITY memorandum generated from a fictional digital-asset scenario and subsequently reviewed for legal accuracy. See how the report identifies factual gaps, organizes regulatory questions, and prepares professionals for further analysis.",
  sampleLabel = "A Real CLARITY Output. Fictional Scenario. Human-Vetted Sample.",
  excerptLabel = "Issue-Mapping Memorandum \u00b7 Sample",
  ctaLabel = "Get the full sample memorandum",
  ctaNote = "Sent as a PDF with the Clarity Issue-Mapping Starter Kit.",
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div data-testid="memo-proof-section">
      <div className="max-w-3xl">
        <SectionLabel>The actual output</SectionLabel>
        <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter font-semibold leading-[0.98] text-ink">{heading}</h2>
        <p className="font-mono text-sm text-ink/80 leading-relaxed mt-5">{supportingCopy}</p>
      </div>

      <div className="mt-10 border-2 border-ink bg-paper">
        {/* Document header */}
        <div className="border-b border-ink px-5 sm:px-8 py-5 bg-white flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/55 mb-2">{excerptLabel}</div>
            <h3 className="font-serif text-xl sm:text-2xl tracking-tight leading-snug text-ink max-w-2xl">{MEMO.scenarioTitle}</h3>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/55 text-right shrink-0">
            <div>{MEMO.memoDate}</div>
            <div className="mt-1">{MEMO.revisionDate}</div>
            <div className="mt-1">File {MEMO.fileId}</div>
            <div className="mt-1">{MEMO.totalIssues} issues mapped</div>
          </div>
        </div>

        {/* Human-vetted sample banner */}
        <div data-testid="memo-proof-vetted-label" className="border-b border-ink bg-blueprint/[0.06] px-5 sm:px-8 py-2.5 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-blueprint">
          {sampleLabel}
        </div>

        {/* Scrollable body */}
        <div className="relative">
          <div
            data-testid="memo-proof-scroll"
            className={`px-5 sm:px-8 py-7 overflow-y-auto overscroll-contain transition-[max-height] duration-500 ${expanded ? "max-h-[3200px]" : "max-h-[560px]"}`}
          >
            {/* 2. Plain-Language Brief */}
            <section data-testid="memo-proof-brief">
              <SectionLabel>Plain-language brief</SectionLabel>
              <p className="font-mono text-[13px] text-ink/85 leading-relaxed max-w-3xl">{MEMO.plainLanguageBrief}</p>
            </section>

            {/* 1. Intake Consistency Notes */}
            <section data-testid="memo-proof-intake" className="mt-8">
              <SectionLabel>Intake consistency notes</SectionLabel>
              <ul className="space-y-2 max-w-3xl">
                {MEMO.intakeConsistencyNotes.map((n) => (
                  <li key={n} className="font-mono text-[13px] text-ink/85 leading-relaxed pl-4 border-l border-ink/20">{n}</li>
                ))}
              </ul>
            </section>

            {/* 3. Eight prioritized regulatory issues */}
            <section data-testid="memo-proof-issues" className="mt-8">
              <SectionLabel>Eight prioritized regulatory issues</SectionLabel>
              <div className="space-y-7">
                {MEMO.issues.map((issue, i) => (
                  <Issue key={`${issue.domain}-${i}`} issue={issue} index={i} />
                ))}
              </div>
            </section>

            {/* 4. Domains Not Surfaced */}
            <section data-testid="memo-proof-not-surfaced" className="mt-10">
              <SectionLabel>Domains not surfaced</SectionLabel>
              <ul className="space-y-3 max-w-3xl">
                {MEMO.domainsNotSurfaced.map((d) => (
                  <li key={d.domain} className="font-mono text-[13px] leading-relaxed">
                    <span className="text-ink font-semibold uppercase tracking-[0.12em] text-[11px]">{d.domain}. </span>
                    <span className="text-ink/80">{d.explanation}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* 5. Selected Regulatory and Legal Sources */}
            <section data-testid="memo-proof-sources" className="mt-10">
              <SectionLabel>Selected regulatory and legal sources</SectionLabel>
              <ul className="space-y-1.5 max-w-3xl list-disc list-outside pl-5">
                {MEMO.selectedSources.map((s) => (
                  <li key={s} className="font-mono text-[12px] text-ink/80 leading-relaxed">{s}</li>
                ))}
              </ul>
            </section>
          </div>

          {!expanded && (
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-paper via-paper/85 to-transparent" />
          )}
        </div>

        {/* Controls */}
        <div className="border-t border-ink px-5 sm:px-8 py-4 bg-white flex flex-wrap items-center gap-3">
          <button
            data-testid="memo-proof-expand"
            onClick={() => {
              setExpanded((v) => !v);
              track("sample_memo_request_click", { source, action: expanded ? "collapse" : "expand" });
            }}
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] border border-ink px-5 py-3 text-ink hover:bg-ink hover:text-white transition-colors"
          >
            {expanded ? "Collapse the preview" : "Read the full preview"}
          </button>
          <a
            href={SAMPLE_PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="memo-proof-open-pdf"
            onClick={() => track("sample_memo_request_click", { source, action: "open_pdf" })}
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] border border-ink px-5 py-3 text-ink hover:bg-ink hover:text-white transition-colors"
          >
            <ExternalLink size={13} strokeWidth={1.75} /> Open the full sample (PDF)
          </a>
          <Link
            to="/checklist"
            data-testid="memo-proof-download-cta"
            onClick={() => track("pricing_sample_memo_click", { source })}
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] bg-ink text-white border border-ink px-5 py-3 hover:bg-blueprint hover:border-blueprint transition-colors"
          >
            <FileText size={13} strokeWidth={1.75} /> {ctaLabel}
            <ArrowRight size={13} strokeWidth={1.75} />
          </Link>
          <span className="font-mono text-[10.5px] text-ink/55 leading-relaxed">{ctaNote}</span>
        </div>
      </div>
    </div>
  );
};

export default MemoProofSection;
